// src/components/CustomerSuggestInput.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { customerSuggest } from '../api/orders.api';

function cx(...a) {
  return a.filter(Boolean).join(' ');
}

export default function CustomerSuggestInput({
  valueName,
  valuePhone,
  onChangeName,
  onChangePhone,
  onPickCustomer,     // (customer) => void  -> set customer_uid
  pickedCustomer,     // {uid,name,phone} | null
  placeholder = "Nom client…",
  showPhoneInput = true,
  inputClassName = '',
  dropdownClassName = '',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(valueName || '');
  const [debounced, setDebounced] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const boxRef = useRef(null);
  const abortRef = useRef(null);

  // sync externe -> input
  useEffect(() => {
    setQ(valueName || '');
  }, [valueName]);

  // debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(String(q || '').trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;

    async function run() {
      const s = String(debounced || '').trim();
      if (!s) {
        setRows([]);
        return;
      }

      // 💡 tu veux "A" => propose tout ce qui commence par A
      // donc on autorise dès 1 caractère, mais LIMIT 10 + debounce
      setLoading(true);

      try {
        abortRef.current?.abort?.();
        abortRef.current = new AbortController();

        // suggestCustomers utilise apiFetch, pas fetch direct:
        // donc on ne peut pas passer signal facilement.
        // => pas grave: debounce + limit évite de surcharger.
        const data = await customerSuggest(s, 10);

        const list = Array.isArray(data?.rows) ? data.rows : [];
        if (!alive) return;
        setRows(list);
        setOpen(true);
      } catch (e) {
        if (!alive) return;
        setRows([]);
      } finally {
        if (!alive) return;
        setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [debounced]);

  const topRows = useMemo(() => rows.slice(0, 10), [rows]);

  function pick(c) {
    onPickCustomer?.(c);
    onChangeName?.(c?.name || '');
    onChangePhone?.(c?.phone || '');
    setOpen(false);
  }

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex gap-2 flex-wrap items-center">
        <input
          value={q}
          onChange={(e) => {
            const v = e.target.value;
            setQ(v);
            onChangeName?.(v);
            // si l'utilisateur retape, on considère qu'il change de client
            if (pickedCustomer) onPickCustomer?.(null);
          }}
          onFocus={() => {
            if (topRows.length) setOpen(true);
          }}
          onBlur={() => {
            // laisse le temps au click dans la liste
            setTimeout(() => setOpen(false), 150);
          }}
          placeholder={placeholder}
          className={cx(
            "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100",
            inputClassName
          )}
        />

        {(pickedCustomer?.uid || pickedCustomer?.id) && (
          <span className="text-xs px-2 py-1 rounded border bg-green-50 text-green-700">
            ✅ Client existant
          </span>
        )}
      </div>

      {/* Suggest list */}
      {open && (loading || topRows.length > 0) && (
        <div
          className={cx(
            "absolute z-50 mt-1 w-full rounded-2xl border border-slate-200 bg-white shadow-sm max-h-72 overflow-auto",
            dropdownClassName
          )}
        >
          {loading && (
            <div className="p-2 text-xs text-content-secondary">Recherche…</div>
          )}

          {!loading && topRows.length === 0 && (
            <div className="p-2 text-xs text-content-secondary">Aucun résultat</div>
          )}

          {!loading &&
            topRows.map((c) => (
              <button
                key={c.uid || c.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => pick(c)}
                className={cx(
                  "w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                )}
              >
                <div className="font-medium">{c.name || '—'}</div>
                <div className="text-xs text-content-secondary">
                  {c.phone || '—'} {c.email ? `• ${c.email}` : ''} {c.total_orders != null ? `• Cmd: ${c.total_orders}` : ''}
                </div>
              </button>
            ))}
        </div>
      )}

      {/* Téléphone */}
      {showPhoneInput && (
        <div className="mt-2">
          <div className="text-xs text-content-secondary mb-1">Téléphone</div>
          <input
            value={valuePhone || ''}
            onChange={(e) => onChangePhone?.(e.target.value)}
            className={cx(
              "w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-slate-300 focus:ring-4 focus:ring-slate-100",
              inputClassName
            )}
            placeholder="Téléphone…"
          />
        </div>
      )}
    </div>
  );
}
